import NoDataDonutChart from "@/components/common/NoDataDonutChart";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDashboard } from "@/contexts/DashboardContext";
import React, { useState, useEffect } from "react";
import { Cell, Legend, Pie, PieChart, Tooltip as RechartsTooltip, ResponsiveContainer, Label } from 'recharts';
import { Info } from "lucide-react";
import { CHART_PALETTE_DARK, CHART_PALETTE_LIGHT } from "@/constants/colors";

interface CallsHandledByQueueWidgetData {
  queueName: string;
  totalHandledCalls: number;
}

const CallsHandledByQueueWidget: React.FC<{ data: CallsHandledByQueueWidgetData[], loading?: boolean; error?: string }> = ({ data, loading }) => {
  const { theme } = useDashboard();
  const [showTotal, setShowTotal] = useState(false);
  const isNoData = !data || data.length === 0 || data.every(item => item.totalHandledCalls === 0);
  const currentColors = theme === 'dark' ? CHART_PALETTE_DARK : CHART_PALETTE_LIGHT;
  const total = data?.reduce((sum, item) => sum + item.totalHandledCalls, 0) || 0;

  useEffect(() => {
    if (!loading && !isNoData && total > 0) {
      const timer = setTimeout(() => {
        setShowTotal(true);
      }, 1000); // Delay 1 second for animation to complete

      return () => clearTimeout(timer);
    } else {
      setShowTotal(false);
    }
  }, [loading, isNoData, total]);

  return (
    <div className="w-full min-w-[200px] rounded-lg border flex flex-col bg-white dark:bg-[#0C3451] dark:text-white dark:border-[#0C3451] p-3">
      <div className="flex flex-row items-center gap-2">
        <div className="text-[14px] font-[500] text-primary dark:text-white truncate">Inbound Handled By Call Queue</div>
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="cursor-pointer">
                <Info className="h-4 w-4 text-primary dark:text-white" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="max-w-xs text-xs">
                The percentage of total number of inbound calls distributed to each Call Queue within the given time.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="h-full min-h-56">
        {loading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <div className="flex-1 flex items-center justify-center min-h-[160px]">
            <div className="w-full h-56">
              {isNoData ? (
                <NoDataDonutChart
                  width="100%"
                  height="100%"
                  innerRadius={55}
                  outerRadius={70}
                  noDataColor="#E5E7EB"
                  noDataText="No data"
                />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="totalHandledCalls"
                      nameKey="queueName"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={70}
                      stroke="none"
                    >
                      {Array.isArray(data) && data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={currentColors[index % currentColors.length]} stroke="none" />
                      ))}
                      {/* {showTotal && (
                        <Label
                          value={total}
                          position="center"
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            fill: theme === 'dark' ? '#ffffff' : '#374151'
                          }}
                        />
                      )} */}
                    </Pie>
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      iconType="rect"
                      wrapperStyle={{
                        right: 0,
                        padding: '8px',
                        margin: 0,
                        width: 'auto',
                        minWidth: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        gap: '4px'
                      }}
                      formatter={(value) => {
                        const colorIndex = data.findIndex(item => item.queueName === value);
                        const dataItem = data.find(item => item.queueName === value);

                        const maxTextLength = 15;
                        const displayName = value.length > maxTextLength ? value.substring(0, maxTextLength) + '...' : value;
                        return <span style={{ color: currentColors[colorIndex] }}>
                          {value.length > maxTextLength ? (
                            <TooltipProvider>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <span>{displayName}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <span>{value} : {dataItem?.totalHandledCalls || 0}</span>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : value} : {dataItem?.totalHandledCalls || 0}
                        </span>
                      }}
                    />
                    <RechartsTooltip
                      formatter={(value: number, name: string, props: { payload: unknown }) => {
                        const { payload } = props;
                        const typedPayload = payload as { percent?: number; queueName?: string };
                        const percent = typedPayload && typedPayload.percent ? (typedPayload.percent * 100).toFixed(0) : null;
                        return percent !== null
                          ? [`${value} (${percent}%)`, typedPayload.queueName]
                          : [value, typedPayload.queueName];
                      }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const { queueName, totalHandledCalls } = payload[0].payload;
                          const total = data.reduce((sum, item) => sum + item.totalHandledCalls, 0);
                          const percent = Number(((totalHandledCalls / total) * 100).toFixed(1));
                          return (
                            <div className="bg-white border border-gray-200 dark:bg-gray-800 dark:border-gray-600 p-2 rounded-lg">
                              <span>{`${queueName}: ${totalHandledCalls} (${percent}%)`}</span>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CallsHandledByQueueWidget;
